import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import certifyMeImg from "../../assets/images/certifyMe-img.png";
import userImg from "../../assets/images/user.png";
import { useNavigate } from "react-router-dom";
import userProfileImage from "../../assets/images/Profile-thumbnail-small.svg";
import ReactMarkdown from "react-markdown";
import { Alert, Image } from "react-bootstrap";
import {
  getCertificationReadyCoursesAPI,
  studentCertifyMeAPI,
  getCertifyMeHistoryAPI,
  checkCertificationPassAPI,
} from "../../redux/features/studentAiSlice";

const StudentCeritifyMe = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const auth = useSelector((state) => state.auth);

  const {
    isLoading = false,
    isError = null,
    message = null,
    certificationReadyCourses = null,
    certifyMeHistory = null,
  } = useSelector((state) => state.studentAI || {});
  const token = useSelector((state) => state.auth.token);

  // State management
  const [input, setInput] = useState("");
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [history, setHistory] = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [isAssessmentComplete, setIsAssessmentComplete] = useState(false);
  const [assessmentResult, setAssessmentResult] = useState(null);
  const [showCourseSelection, setShowCourseSelection] = useState(true);
  const [showChat, setShowChat] = useState(false);
  const [questionCount, setQuestionCount] = useState(0);
  const [completedAssessments, setCompletedAssessments] = useState(new Set());
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [selectedCourseForWarning, setSelectedCourseForWarning] =
    useState(null);
  const [showEndAssessmentModal, setShowEndAssessmentModal] = useState(false);
  const [retakeTimers, setRetakeTimers] = useState({});
  const [isAutoTyping, setIsAutoTyping] = useState(false);
  const [autoTypedText, setAutoTypedText] = useState("");

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [cardsPerPage] = useState(6);

  // Audio recording states
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [recognition, setRecognition] = useState(null);
  const [audioDuration, setAudioDuration] = useState(0);
  const [recordingStartTime, setRecordingStartTime] = useState(null);
  const [transcribedText, setTranscribedText] = useState("");

  const chatContainerRef = useRef(null);
  const textareaRef = useRef(null);

  // Load certification ready courses on component mount
  useEffect(() => {
    if (token) {
      dispatch(getCertificationReadyCoursesAPI({ token }));
    }
  }, [token, dispatch]);

  // Check for completed assessments when courses are loaded
  useEffect(() => {
    if (certificationReadyCourses && token) {
      checkCompletedAssessments();
      // Reset to first page when courses are loaded
      setCurrentPage(1);
    }
  }, [certificationReadyCourses, token]);

  const checkCompletedAssessments = async () => {
    if (!certificationReadyCourses) return;

    console.log(
      "Checking completed assessments for courses:",
      certificationReadyCourses.length
    );
    const completed = new Set();

    // Use the assessmentResult data from the API response
    for (const course of certificationReadyCourses) {
      if (course.assessmentResult) {
        completed.add(course.courseId);
        console.log(
          "Course completed:",
          course.courseId,
          "Score:",
          course.assessmentResult.score,
          "Passed:",
          course.assessmentResult.passed
        );
      }
    }

    console.log("Completed assessments found:", Array.from(completed));
    setCompletedAssessments(completed);
  };

  // Load conversation history when course is selected
  useEffect(() => {
    if (selectedCourse && token) {
      dispatch(
        getCertifyMeHistoryAPI({
          token,
          courseId: selectedCourse.courseId,
          page: 1,
          limit: 50,
        })
      );
    }
  }, [selectedCourse, token, dispatch]);

  // Update local history when conversation history is loaded
  useEffect(() => {
    if (certifyMeHistory && certifyMeHistory.sessions) {
      const allMessages = [];
      certifyMeHistory.sessions.forEach((session) => {
        session.messages.forEach((message) => {
          allMessages.push({
            type: message.type,
            text: message.content,
            timestamp: message.timestamp,
            sessionId: session.sessionId,
            competencyArea: message.competencyArea,
            score: message.score,
          });
        });
      });

      // Sort messages by timestamp to show in chronological order (oldest first)
      allMessages.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

      setHistory(allMessages);

      // Set current session ID from the latest session
      if (allMessages.length > 0) {
        setCurrentSessionId(allMessages[allMessages.length - 1].sessionId);
      }
    }
  }, [certifyMeHistory]);

  // Auto-scroll to the latest message
  useEffect(() => {
    if (chatContainerRef.current) {
      // Use setTimeout to ensure DOM is updated before scrolling
      setTimeout(() => {
        chatContainerRef.current.scrollTop =
          chatContainerRef.current.scrollHeight;
      }, 100);
    }
  }, [history]);

  // Auto-scroll when chat view is shown
  useEffect(() => {
    if (showChat && chatContainerRef.current) {
      // Scroll to bottom when chat view is opened
      setTimeout(() => {
        chatContainerRef.current.scrollTop =
          chatContainerRef.current.scrollHeight;
      }, 200);
    }
  }, [showChat]);

  // Auto-typing effect when chat opens
  useEffect(() => {
    if (showChat && selectedCourse && history.length === 0) {
      const textToType = "Start my assessment";
      let currentIndex = 0;
      setIsAutoTyping(true);
      setAutoTypedText("");

      const typingInterval = setInterval(() => {
        if (currentIndex < textToType.length) {
          setAutoTypedText(textToType.substring(0, currentIndex + 1));
          currentIndex++;
        } else {
          clearInterval(typingInterval);
          setIsAutoTyping(false);
          setInput(textToType); // Set the input field with the typed text
        }
      }, 100); // Type each character every 100ms

      return () => clearInterval(typingInterval);
    }
  }, [showChat, selectedCourse, history.length]);

  // Timer for recording duration
  useEffect(() => {
    let interval = null;
    if (isRecording && recordingStartTime) {
      interval = setInterval(() => {
        setAudioDuration(Math.floor((Date.now() - recordingStartTime) / 1000));
      }, 1000);
    } else {
      setAudioDuration(0);
    }
    return () => clearInterval(interval);
  }, [isRecording, recordingStartTime]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      const newHeight = Math.min(textareaRef.current.scrollHeight, 200);
      textareaRef.current.style.height = `${newHeight}px`;
    }
  }, [input]);

  // Timer for retake countdown
  useEffect(() => {
    const interval = setInterval(() => {
      if (certificationReadyCourses) {
        const newTimers = {};
        let hasActiveTimers = false;

        certificationReadyCourses.forEach((course) => {
          if (
            course.assessmentResult &&
            !course.assessmentResult.passed &&
            course.assessmentResult.retakeInfo
          ) {
            const now = new Date();
            const completed = new Date(course.assessmentResult.completedAt);
            const nextRetake = new Date(
              completed.getTime() + 24 * 60 * 60 * 1000
            );
            const timeDiff = nextRetake - now;

            if (timeDiff > 0) {
              const hours = Math.floor(timeDiff / (1000 * 60 * 60));
              const minutes = Math.floor(
                (timeDiff % (1000 * 60 * 60)) / (1000 * 60)
              );
              const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);

              newTimers[course.courseId] = {
                hours,
                minutes,
                seconds,
                total: timeDiff,
              };
              hasActiveTimers = true;
            } else {
              newTimers[course.courseId] = { canRetake: true };
            }
          }
        });

        setRetakeTimers(newTimers);

        if (!hasActiveTimers) {
          clearInterval(interval);
        }
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [certificationReadyCourses]);

  const handleInputChange = (e) => {
    setInput(e.target.value);
  };

  // Check if assessment is complete based on AI response content
  const checkAssessmentComplete = (aiResponse) => {
    const completionKeywords = [
      "assessment complete",
      "certification recommendation",
      "evaluation complete",
      "assessment concluded",
      "final evaluation",
      "certification decision",
      "assessment finished",
      "certification assessment complete",
      "final assessment",
      "evaluation finished",
    ];

    const responseText = aiResponse.toLowerCase();
    const hasCompletionKeywords = completionKeywords.some((keyword) =>
      responseText.includes(keyword)
    );

    // Also check if user explicitly wants to end assessment
    const userWantsToEnd =
      responseText.includes("end assessment") ||
      responseText.includes("stop assessment") ||
      responseText.includes("finish assessment");

    // Force completion after 1 question (single question assessment)
    const maxQuestionsReached = questionCount >= 1;

    return hasCompletionKeywords || userWantsToEnd || maxQuestionsReached;
  };

  const handleSubmit = async () => {
    if (!selectedCourse) return;
    if (!token) {
      dispatch({
        type: "studentAI/studentCertifyMeAPI/rejected",
        payload: "Authentication required: Please log in to continue.",
      });
      navigate("/login");
      return;
    }

    // If no input, send empty string to trigger first question
    const inputToSend = input.trim() || "";

    // Add user message immediately to local history (only if there's actual input)
    if (input.trim()) {
      const userMessage = {
        type: "user",
        text: input,
        timestamp: new Date().toISOString(),
      };
      setHistory((prev) => [...prev, userMessage]);
    }

    try {
      const result = await dispatch(
        studentCertifyMeAPI({
          token,
          input: inputToSend,
          courseId: selectedCourse.courseId,
          sessionId: currentSessionId,
        })
      ).unwrap();

      if (result.success) {
        // Add AI response to local history
        const aiMessage = {
          type: "ai",
          text: result.data?.response || "No response content received.",
          sessionId: result.data?.sessionId,
          competencyArea: result.data?.competencyAreas?.join(", "),
          score: result.data?.score,
          timestamp: new Date().toISOString(),
        };
        setHistory((prev) => [...prev, aiMessage]);

        // Track current session
        setCurrentSessionId(result.data?.sessionId);

        // Check if assessment is complete
        const isComplete = checkAssessmentComplete(aiMessage.text);

        // Only increment question count for actual questions (not final evaluation)
        if (aiMessage.text && !isAssessmentComplete && !isComplete) {
          setQuestionCount((prev) => prev + 1);
        }
        if (isComplete && !isAssessmentComplete) {
          setIsAssessmentComplete(true);
          // Check certification pass status
          const passResult = await dispatch(
            checkCertificationPassAPI({
              token,
              courseId: selectedCourse.courseId,
              sessionId: result.data?.sessionId,
            })
          ).unwrap();

          if (passResult.success) {
            setAssessmentResult(passResult.data);
          }
        }

        // Refresh conversation history from database
        dispatch(
          getCertifyMeHistoryAPI({
            token,
            courseId: selectedCourse.courseId,
            page: 1,
            limit: 50,
          })
        );
      }
    } catch (err) {
      console.error("Error submitting message:", err);
    } finally {
      setInput("");
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    }
  };

  const handleClear = () => {
    setHistory([]);
    setIsAssessmentComplete(false);
    setAssessmentResult(null);
    setCurrentSessionId(null);
    setShowChat(false);
    setShowCourseSelection(true);
    setQuestionCount(0);
  };

  // Audio recording functions
  const startRecording = () => {
    if ("webkitSpeechRecognition" in window || "SpeechRecognition" in window) {
      const SpeechRecognition =
        window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognition = new SpeechRecognition();

      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = "en-US";

      recognition.onstart = () => {
        setIsRecording(true);
        setRecordingStartTime(Date.now());
        setAudioDuration(0);
      };

      recognition.onresult = (event) => {
        let finalTranscript = "";
        let interimTranscript = "";

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript;
          } else {
            interimTranscript += transcript;
          }
        }

        setTranscribedText(finalTranscript || interimTranscript);
      };

      recognition.onerror = (error) => {
        console.error("Speech recognition error:", error);
        setIsRecording(false);
        setRecordingStartTime(null);
        alert("Speech recognition error. Please try again.");
      };

      recognition.onend = () => {
        setIsRecording(false);
        setRecordingStartTime(null);
      };

      recognition.start();
      setRecognition(recognition);
    } else {
      alert(
        "Speech recognition not supported in this browser. Please type your message."
      );
    }
  };

  const stopRecording = () => {
    if (recognition) {
      recognition.stop();
      setRecognition(null);
    }
    setIsRecording(false);
    setRecordingStartTime(null);
  };

  const sendAudioMessage = async () => {
    try {
      setIsProcessing(true);

      const userMessage = {
        type: "user",
        text: `🎤 Audio message (${audioDuration}s)`,
        timestamp: new Date().toISOString(),
        isAudio: true,
        audioDuration: audioDuration,
      };
      setHistory((prev) => [...prev, userMessage]);

      const textToSend = transcribedText.trim() || "I sent an audio message";
      const result = await dispatch(
        studentCertifyMeAPI({
          token,
          input: textToSend,
          courseId: selectedCourse.courseId,
          sessionId: currentSessionId,
        })
      ).unwrap();

      if (result.success) {
        const aiMessage = {
          type: "ai",
          text: result.data?.response || "No response content received.",
          sessionId: result.data?.sessionId,
          competencyArea: result.data?.competencyAreas?.join(", "),
          score: result.data?.score,
          timestamp: new Date().toISOString(),
        };
        setHistory((prev) => [...prev, aiMessage]);

        setCurrentSessionId(result.data?.sessionId);

        const isComplete = checkAssessmentComplete(aiMessage.text);
        if (isComplete && !isAssessmentComplete) {
          setIsAssessmentComplete(true);
          const passResult = await dispatch(
            checkCertificationPassAPI({
              token,
              courseId: selectedCourse.courseId,
              sessionId: result.data?.sessionId,
            })
          ).unwrap();

          if (passResult.success) {
            setAssessmentResult(passResult.data);
          }
        }

        dispatch(
          getCertifyMeHistoryAPI({
            token,
            courseId: selectedCourse.courseId,
            page: 1,
            limit: 50,
          })
        );
      }
    } catch (error) {
      console.error("Error sending audio message:", error);
      alert("Error sending message. Please try again.");
    } finally {
      setIsProcessing(false);
      setAudioDuration(0);
      setTranscribedText("");
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    }
  };

  const handleMicrophoneClick = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  const handleSendClick = () => {
    if (isRecording) {
      stopRecording();
      sendAudioMessage();
    } else if (audioDuration > 0) {
      sendAudioMessage();
    } else {
      handleSubmit();
    }
  };

  const handleCourseSelect = (course) => {
    // Check if assessment is already completed
    if (completedAssessments.has(course.courseId)) {
      setSelectedCourseForWarning(course);
      setShowWarningModal(true);
      return;
    }

    setSelectedCourse(course);
    setShowCourseSelection(false);
    setShowChat(true);
    setHistory([]);
    setIsAssessmentComplete(false);
    setAssessmentResult(null);
    setCurrentSessionId(null);
    setQuestionCount(0);

    // Automatically start the assessment by sending empty input
    setTimeout(() => {
      handleSubmit();
    }, 100);
  };

  const handleConfirmEndAssessment = () => {
    if (selectedCourseForWarning) {
      setSelectedCourse(selectedCourseForWarning);
      setShowCourseSelection(false);
      setShowChat(true);
      setHistory([]);
      setIsAssessmentComplete(false);
      setAssessmentResult(null);
      setCurrentSessionId(null);
      setQuestionCount(0);
    }
    setShowWarningModal(false);
    setSelectedCourseForWarning(null);
  };

  const handleCancelEndAssessment = () => {
    setShowWarningModal(false);
    setSelectedCourseForWarning(null);
  };

  const handleConfirmEndAssessmentModal = async () => {
    try {
      // Mark assessment as failed and end it immediately
      setIsAssessmentComplete(true);
      setAssessmentResult({
        passed: false,
        message: "Assessment ended by user request",
        averageScore: 0,
        totalQuestions: questionCount,
      });
      setShowEndAssessmentModal(false);

      // Save the failed assessment to database
      if (selectedCourse && currentSessionId && token) {
        await dispatch(
          checkCertificationPassAPI({
            token,
            courseId: selectedCourse.courseId,
            sessionId: currentSessionId,
          })
        );

        // Add to completed assessments to prevent retaking
        setCompletedAssessments(
          (prev) => new Set([...prev, selectedCourse.courseId])
        );
      }
    } catch (error) {
      console.error("Error ending assessment:", error);
    }
  };

  const handleCancelEndAssessmentModal = () => {
    setShowEndAssessmentModal(false);
  };

  const handleBackToCourses = () => {
    setShowCourseSelection(true);
    setShowChat(false);
    setSelectedCourse(null);
    setHistory([]);
    setIsAssessmentComplete(false);
    setAssessmentResult(null);
    setCurrentSessionId(null);
    setQuestionCount(0);
  };

  // Pagination logic
  const getPaginatedCourses = () => {
    if (!certificationReadyCourses) return [];
    const startIndex = (currentPage - 1) * cardsPerPage;
    const endIndex = startIndex + cardsPerPage;
    return certificationReadyCourses.slice(startIndex, endIndex);
  };

  const getTotalPages = () => {
    if (!certificationReadyCourses) return 0;
    return Math.ceil(certificationReadyCourses.length / cardsPerPage);
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    // Scroll to top of course cards when page changes
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleNextPage = () => {
    const totalPages = getTotalPages();
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  if (!useSelector((state) => state.studentAI)) {
    return (
      <div className="text-center text-red-600">
        Error: Student AI reducer not found. Please check Redux store
        configuration.
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="loader-container">
        <div className="dottedLoading theme-color" />
      </div>
    );
  }
  return (
    <>
      <style>
        {`
          .wave-animation {
            display: flex;
            align-items: center;
            gap: 2px;
          }
          
          .wave-bar {
            width: 3px;
            height: 20px;
            background-color: #dc3545;
            border-radius: 2px;
            animation: wave 1.2s ease-in-out infinite;
          }
          
          .wave-bar:nth-child(1) { animation-delay: 0s; }
          .wave-bar:nth-child(2) { animation-delay: 0.1s; }
          .wave-bar:nth-child(3) { animation-delay: 0.2s; }
          .wave-bar:nth-child(4) { animation-delay: 0.3s; }
          .wave-bar:nth-child(5) { animation-delay: 0.4s; }
          
          @keyframes wave {
            0%, 40%, 100% {
              transform: scaleY(0.4);
            }
            20% {
              transform: scaleY(1);
            }
          }
          
          .course-card {
            transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
            border-radius: 24px;
            overflow: hidden;
            position: relative;
            background: linear-gradient(145deg, #ffffff 0%, #f8f9fa 50%, #e9ecef 100%);
            height: 100%;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            border: 2px solid transparent;
            box-shadow: 
              0 8px 32px rgba(0,0,0,0.08),
              0 4px 16px rgba(0,0,0,0.04),
              inset 0 1px 0 rgba(255,255,255,0.8);
            backdrop-filter: blur(10px);
            -webkit-backdrop-filter: blur(10px);
          }
          
          .course-card:hover {
            transform: translateY(-8px) scale(1);
            box-shadow: 
              0 25px 80px rgba(0,0,0,0.15),
              0 15px 40px rgba(0,0,0,0.1),
              inset 0 1px 0 rgba(255,255,255,0.9);
          }
          
.course-card.border-primary{
           border-color: #82bd00 !important;
          }
          .course-card.border-warning{
           border-color: #f05203 !important;
          }

          .progress-ring {
            transform: rotate(-90deg);
            filter: drop-shadow(0 4px 8px rgba(0,0,0,0.1));
          }
          
          .progress-ring-circle {
            transition: stroke-dashoffset 0.8s cubic-bezier(0.4, 0, 0.2, 1);
            transform-origin: 50% 50%;
            stroke-linecap: round;
            filter: drop-shadow(0 2px 4px rgba(0,0,0,0.2));
          }
          
          .requirement-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 6px 8px;
            margin: 2px 0;
            border-radius: 8px;
            background: linear-gradient(135deg, rgba(248, 249, 250, 0.9), rgba(233, 236, 239, 0.7));
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
            border: 1px solid rgba(0,0,0,0.05);
            position: relative;
            overflow: hidden;
          }
          
          .requirement-item::before {
            content: '';
            position: absolute;
            top: 0;
            left: -100%;
            width: 100%;
            height: 100%;
            background: linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent);
            transition: left 0.6s;
          }
          
          .requirement-item:hover {
            background: linear-gradient(135deg, rgba(248, 249, 250, 1), rgba(233, 236, 239, 0.9));
            transform: translateX(4px) scale(1.02);
            box-shadow: 0 4px 12px rgba(0,0,0,0.1);
          }
          
          .requirement-item:hover::before {
            left: 100%;
          }
          
          .requirement-item.completed {
            background: #82BD001A;
            border-left: 4px solid #82bd00;
          }
          
          .requirement-item.completed:hover {
            background: linear-gradient(135deg, rgba(40, 167, 69, 0.2), rgba(32, 201, 151, 0.15));
            box-shadow: 0 6px 16px rgba(40, 167, 69, 0.3);
          }
          
          .requirement-item.in-progress {
            background: linear-gradient(135deg, rgba(255, 193, 7, 0.15), rgba(253, 126, 20, 0.1));
            border-left: 4px solid #ffc107;
            box-shadow: 0 2px 8px rgba(255, 193, 7, 0.2);
          }
          
          .requirement-item.in-progress:hover {
            background: linear-gradient(135deg, rgba(255, 193, 7, 0.2), rgba(253, 126, 20, 0.15));
            box-shadow: 0 6px 16px rgba(255, 193, 7, 0.3);
          }
          
          .requirement-label {
            font-weight: 500;
            display: flex;
            align-items: center;
            gap: 6px;
          }

          .requirement-item.completed .requirement-label{
          color:#82bd00;
          }
          .requirement-item.in-progress .requirement-label{
          color:#ffc107;
          }
          
          .requirement-value {
            font-weight: 500;
            font-size: 0.9em;
          }
          
          .requirement-icon {
            width: 16px;
            height: 16px;
            opacity: 0.7;
          }
          
          .course-badge {
            width: 145px;
            position: absolute;
            top: 14px;
            right: -43px;
            transform: rotate(45deg);
            padding: 8px 16px;
            font-size: 0.65em;
            font-weight: 800;
            text-transform: uppercase;
            letter-spacing: 0.8px;
            z-index: 2;
            backdrop-filter: blur(10px);
            border: 1px solid rgba(255,255,255,0.2);
          }
          
          .course-badge.bg-success {
            background: linear-gradient(135deg, #28a745, #20c997);
            color: white;
            text-shadow: 0 1px 2px rgba(0,0,0,0.3);
          }
          
          .course-badge.bg-green {
            background: #82bd00 !important;
            color: #212529;
            text-shadow: 0 1px 2px rgba(255,255,255,0.5);
          }
          
          .course-badge.bg-primary {
            background: #f05203 !important;
            color: white;
            text-shadow: 0 1px 2px rgba(0,0,0,0.3);
          }
          
          
          /* Enhanced grid layout */
          .requirements-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 8px;
            margin-top: 8px;
          }
          
          /* Progress percentage enhancement */
          .progress-percentage {
            position: absolute;
            font-size: 0.85em;
            font-weight: 800;
            color: #495057;
            text-shadow: 0 1px 2px rgba(0,0,0,0.1);
            transition: all 0.3s ease;
          }
          
          /* Enhanced requirement icons */
          .requirement-icon {
            width: 14px;
            height: 14px;
            opacity: 0.8;
            transition: all 0.3s ease;
            filter: drop-shadow(0 1px 2px rgba(0,0,0,0.1));
          }
          
          .requirement-item:hover .requirement-icon {
            opacity: 1;
            transform: scale(1.1) rotate(5deg);
            filter: drop-shadow(0 2px 4px rgba(0,0,0,0.2));
          }
          
          .requirement-item.completed .requirement-icon {
            color: #82bd00;
          }
          
          .requirement-item.in-progress .requirement-icon {
            color: #ffc107;
            filter: drop-shadow(0 2px 4px rgba(255, 193, 7, 0.3));
          }
          
          /* Enhanced requirement values */
          .requirement-value {
            font-weight: 500;
            font-size: 0.8em;
          }
          
          
          .course-header {
            padding: 12px 64px 8px;
            position: relative;
            min-height: 60px;
            display: flex;
            flex-direction: column;
            justify-content: center;
            text-align: center;
            background: linear-gradient(135deg, rgba(255,255,255,0.9), rgba(248,249,250,0.7));
            border-radius: 24px 24px 0 0;
            backdrop-filter: blur(5px);
            -webkit-backdrop-filter: blur(5px);
          }
          
          .course-title {
            font-size: 1em;
            font-weight: 800;
            color: #212529;
            margin-bottom: 4px;
            line-height: 1.2;
            text-align: center;
            text-shadow: 0 1px 2px rgba(0,0,0,0.1);
            letter-spacing: -0.02em;
            transition: all 0.3s ease;
          }
          
          
          .course-subtitle {
            font-size: 0.75em;
            color: #6c757d;
            line-height: 1.3;
            margin: 0;
            text-align: center;
            font-weight: 500;
            opacity: 0.9;
            transition: all 0.3s ease;
            max-height: 2.6em;
            overflow: hidden;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
          }
          
          .course-content {
            padding: 0 12px;
          }
          
          .course-footer {
            padding: 12px;
            display: flex;
            flex-direction: column;
            align-items: center;
            text-align: center;
          }
          
          .progress-container {
            border-radius: 12px;
            padding: 10px;
            margin: 0 0 8px 0;
            transition: all 0.3s ease;
          }
          
          .progress-ring-container {
            display: flex;
            justify-content: center;
            align-items: center;
            margin-bottom: 8px;
          }
          
          .progress-ring-wrapper {
            position: relative;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          
          .progress-percentage {
            position: absolute;
            font-size: 0.9em;
            font-weight: 700;
            color: #495057;
          }
          
          .requirements-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 6px;
          }
          
          /* Course Chat Header Styles */
          .course-chat-header {
        
            border-radius: 20px;
            padding: 0;
            overflow: hidden;
            position: relative;
          }
          
         
          
          .course-header-content {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 25px 30px;
            position: relative;
            z-index: 1;
          }
          
          .course-info {
            flex: 1;
            text-align: left;
          }

          .course-info.text-left {
            text-align: left;
          }

          .header-actions {
            margin-left: 20px;
          }
          
          .course-title {
            color: black;
            font-size: 1.4em;
            font-weight: 700;
            margin-bottom: 8px;
            line-height: 1.3;
            }
          
          .course-subtitle {
            color: black;
            font-size: 0.95em;
            margin: 0;
            line-height: 1.4;
          }
          
          .header-actions {
            display: flex;
            gap: 12px;
            flex-shrink: 0;
          }
          
          .btn-back {
             background: #fff !important;
            border-color: #000 !important;
            color: #000 !important;
            transform: translateY(-2px) !important;
            box-shadow: 0 5px 15px rgba(0,0,0,0.2) !important;
          }
          
          .btn-back:hover {
            background: #fff !important;
            border-color:#000 !important;
            color: #000e !important;
            transform: translateY(-2px) !important;
            box-shadow: 0 5px 15px rgba(0,0,0,0.2) !important;
          }          
          .btn-end-assessment {
               background: rgba(220, 53, 69, 0.3);
            border-color: rgba(220, 53, 69, 0.6);
            color: #ff4757;
            transform: translateY(-2px);
            box-shadow: 0 5px 15px rgba(220, 53, 69, 0.3);
          }
          
          .btn-end-assessment:hover {
            background: rgba(220, 53, 69, 0.3);
            border-color: rgba(220, 53, 69, 0.6);
            color: #ff4757;
            transform: translateY(-2px);
            box-shadow: 0 5px 15px rgba(220, 53, 69, 0.3);
          }
          
          .action-button:hover {
            transform: translateY(-2px);
            box-shadow: 0 6px 20px rgba(0,0,0,0.15);
          }

          .assessment-result {
            text-align: center;
            padding: 8px;
            border-radius: 8px;
            background: linear-gradient(135deg, #f8f9fa, #e9ecef);
            border: 1px solid #dee2e6;
          }

          .result-badge {
            font-size: 0.8em;
            font-weight: 700;
            padding: 4px 10px;
            border-radius: 5px;
            margin-bottom: 4px;
            display: inline-block;
            letter-spacing: 0.3px;
          }

          .result-badge.passed {
            background: #82bd00;
            color: white;
          }

          .result-badge.failed {
            background: #f05203;
            color: white;
          }

          .result-score {
            font-size: 1.1em;
            color: #495057;
            margin-bottom: 4px;
          }

          .result-date {
            font-size: 0.8em;
            color: #6c757d;
            font-style: italic;
          }

          .retake-info {
            padding: 8px;
            border-radius: 8px;
            background: rgba(255, 193, 7, 0.1);
            border: 1px solid rgba(255, 193, 7, 0.3);
          }

          .retake-info .text-green {
            color: #82bd00 !important;
            font-weight: 600;
          }

          .retake-info .text-warning {
            color: #ffc107 !important;
            font-weight: 500;
          }

          .retake-info .text-muted {
            color: #6c757d !important;
          }

          .auto-typing {
            position: relative;
            color: #007bff !important;
            font-weight: 500;
          }

          .auto-typing::after {
            content: '|';
            animation: blink 1s infinite;
            color: #007bff;
            font-weight: bold;
          }

          @keyframes blink {
            0%, 50% { opacity: 1; }
            51%, 100% { opacity: 0; }
          }
          
          /* Pagination Styles */
          .pagination {
            margin: 0;
            gap: 4px;
          }
          
          .page-link {
            border-radius: 8px !important;
            border: 2px solid #dee2e6 !important;
            margin: 0 2px;
            padding: 8px 12px;
            font-weight: 600;
            transition: all 0.3s ease;
            min-width: 40px;
            text-align: center;
          }
          
          .page-link:hover {
            background-color: #e9ecef !important;
            border-color: #007bff !important;
            color: #007bff !important;
            transform: translateY(-1px);
            box-shadow: 0 2px 8px rgba(0, 123, 255, 0.2);
          }
          
          .page-item.active .page-link {
            background-color: #007bff !important;
            border-color: #007bff !important;
            color: white !important;
            box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
          }
          
          .page-item.disabled .page-link {
            background-color: #f8f9fa !important;
            border-color: #e9ecef !important;
            color: #6c757d !important;
            cursor: not-allowed;
            opacity: 0.6;
          }
          
          .page-item.disabled .page-link:hover {
            transform: none;
            box-shadow: none;
          }
          
          .pagination-lg .page-link {
            padding: 10px 16px;
            font-size: 1rem;
          }
        `}
      </style>

      <div className="card-default-style px-10 px-sm-20 px-md-30 px-lg-50 py-10 py-sm-15 py-md-20 py-lg-30">
        <div className="certifyMe text-center">
          <div className="certifyMe-inner mx-auto">
            {/* Course Selection View */}
            {showCourseSelection && (
              <>
                <div className="chat-header mx-auto">
                  <img
                    src={certifyMeImg}
                    className="img-fluid img mx-auto"
                    alt="Certification"
                  />
                  <h2 className="fw-bold my-21 ah-heading-04">CertifyMe.AI</h2>
                  <p className="ah-text-base fw-normal px-10 px-sm-20 px-md-30 text-gray-600">
                    Advanced certification assessment system. Evaluate your
                    mastery of course competencies through comprehensive testing
                    and real-world application scenarios.
                  </p>
                </div>
                <div className="mt-32">
                  <h4 className="mb-36 ah-heading-04 fw-bold black-800 gap-3">
                    Select a Course for Certification Assessment
                  </h4>
                  <div className="row">
                    {certificationReadyCourses &&
                      certificationReadyCourses.length > 0 ? (
                      getPaginatedCourses().map((course, index) => (
                        <div
                          key={index}
                          className="col-12 col-md-6 col-lg-4 mb-24"
                        >
                          <div
                            className={`card course-card ${completedAssessments.has(course.courseId)
                              ? "border-primary"
                              : course.isReady
                                ? "border-success"
                                : "border-warning"
                              }`}
                          >
                            {/* Course Badge */}
                            <span
                              className={`course-badge ${completedAssessments.has(course.courseId)
                                ? "bg-green text-white"
                                : course.isReady
                                  ? "bg-success text-white"
                                  : "bg-primary text-white"
                                }`}
                            >
                              {completedAssessments.has(course.courseId)
                                ? "Completed"
                                : course.isReady
                                  ? "Ready"
                                  : "In Progress"}
                            </span>
                            {/* Course Header */}
                            <div className="course-header">
                              {/* Course Title */}
                              <h5 className="course-title">{course.title}</h5>

                              {/* Course Subtitle */}
                              {course.subtitle && (
                                <p className="course-subtitle">
                                  {course.subtitle}
                                </p>
                              )}
                            </div>

                            {/* Course Content */}
                            <div className="course-content">
                              {/* Progress Ring */}
                              <div className="progress-container">
                                <div className="progress-ring-container">
                                  <div className="progress-ring-wrapper">
                                    <svg
                                      className="progress-ring"
                                      width="70"
                                      height="70"
                                    >
                                      <circle
                                        stroke="#e6e6e6"
                                        strokeWidth="3"
                                        fill="transparent"
                                        r="25"
                                        cx="35"
                                        cy="35"
                                      />

                                      <circle
                                        className="progress-ring-circle"
                                        stroke={
                                          course.isReady ? "#82bd00" : "#f0c20c"
                                        }
                                        strokeWidth="3"
                                        fill="transparent"
                                        r="25"
                                        cx="35"
                                        cy="35"
                                        style={{
                                          strokeDasharray: `${2 * Math.PI * 25
                                            }`,
                                          strokeDashoffset: `${2 *
                                            Math.PI *
                                            25 *
                                            (1 - course.score / 100)
                                            }`,
                                        }}
                                      />
                                    </svg>
                                    <div className="progress-percentage">
                                      {course.score}%
                                    </div>
                                  </div>
                                </div>

                                {/* Requirements Status */}
                                <div className="requirements-grid">
                                  <div
                                    className={`requirement-item ${(course.details?.notes || 0) >=
                                      (course.requirements?.notes?.required || 0)
                                      ? "completed"
                                      : "in-progress"
                                      }`}
                                  >
                                    <div className="requirement-label">
                                      <svg
                                        className="requirement-icon"
                                        viewBox="0 0 24 24"
                                        fill="currentColor"
                                      >
                                        <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                                      </svg>
                                      Notes
                                    </div>
                                    <span
                                      className={`requirement-value ${(course.details?.notes || 0) >=
                                        (course.requirements?.notes?.required || 0)
                                        ? "text-green"
                                        : "text-warning"
                                        }`}
                                    >
                                      {course.details?.notes || 0}/
                                      {course.requirements?.notes?.required || 0}
                                    </span>
                                  </div>

                                  <div
                                    className={`requirement-item ${(course.details?.quizzes || 0) >=
                                      (course.requirements?.quizzes?.required || 0)
                                      ? "completed"
                                      : "in-progress"
                                      }`}
                                  >
                                    <div className="requirement-label">
                                      <svg
                                        className="requirement-icon"
                                        viewBox="0 0 24 24"
                                        fill="currentColor"
                                      >
                                        <path d="M9,22A1,1 0 0,1 8,21V18H4A2,2 0 0,1 2,16V4C2,2.89 2.9,2 4,2H20A2,2 0 0,1 22,4V16A2,2 0 0,1 20,18H13.9L10.2,21.71C10,21.9 9.75,22 9.5,22V22H9M10,16V19.08L13.08,16H20V4H4V16H10Z" />
                                      </svg>
                                      Quizzes
                                    </div>
                                    <span
                                      className={`requirement-value ${(course.details?.quizzes || 0) >=
                                        (course.requirements?.quizzes?.required || 0)
                                        ? "text-green"
                                        : "text-warning"
                                        }`}
                                    >
                                      {course.details?.quizzes || 0}/
                                      {course.requirements?.quizzes?.required || 0}
                                    </span>
                                  </div>

                                  <div
                                    className={`requirement-item ${(course.details?.flashcardSets || 0) >=
                                      (course.requirements?.flashcards?.required || 0)
                                      ? "completed"
                                      : "in-progress"
                                      }`}
                                  >
                                    <div className="requirement-label">
                                      <svg
                                        className="requirement-icon"
                                        viewBox="0 0 24 24"
                                        fill="currentColor"
                                      >
                                        <path d="M19,3H5C3.89,3 3,3.89 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5C21,3.89 20.1,3 19,3M19,19H5V5H19V19Z" />
                                      </svg>
                                      Flashcards
                                    </div>
                                    <span
                                      className={`requirement-value ${(course.details?.flashcardSets || 0) >=
                                        (course.requirements?.flashcards?.required || 0)
                                        ? "text-green"
                                        : "text-warning"
                                        }`}
                                    >
                                      {course.details?.flashcardSets || 0}/
                                      {course.requirements?.flashcards?.required || 0}
                                    </span>
                                  </div>

                                  <div
                                    className={`requirement-item ${(course.details?.learningHours || 0) >=
                                      (course.requirements?.learningHours?.required || 0)
                                      ? "completed"
                                      : "in-progress"
                                      }`}
                                  >
                                    <div className="requirement-label">
                                      <svg
                                        className="requirement-icon"
                                        viewBox="0 0 24 24"
                                        fill="currentColor"
                                      >
                                        <path d="M12,20A8,8 0 0,0 20,12A8,8 0 0,0 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22C6.47,22 2,17.5 2,12A10,10 0 0,1 12,2M12.5,7V12.25L17,14.92L16.25,16.15L11,13V7H12.5Z" />
                                      </svg>
                                      Learning Hours
                                    </div>
                                    <span
                                      className={`requirement-value ${(course.details?.learningHours || 0) >=
                                        (course.requirements?.learningHours?.required || 0)
                                        ? "text-green"
                                        : "text-warning"
                                        }`}
                                    >
                                      {course.details?.learningHours || 0}h/
                                      {course.requirements?.learningHours?.required || 0}h
                                    </span>
                                  </div>

                                  <div
                                    className={`requirement-item ${(course.details?.avgQuizScore || 0) >=
                                      (course.requirements?.quizScore?.required || 0)
                                      ? "completed"
                                      : "in-progress"
                                      }`}
                                  >
                                    <div className="requirement-label">
                                      <svg
                                        className="requirement-icon"
                                        viewBox="0 0 24 24"
                                        fill="currentColor"
                                      >
                                        <path d="M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M11,16.5L18,9.5L16.5,8L11,13.5L7.5,10L6,11.5L11,16.5Z" />
                                      </svg>
                                      Quiz Score
                                    </div>
                                    <span
                                      className={`requirement-value ${(course.details?.avgQuizScore || 0) >=
                                        (course.requirements?.quizScore?.required || 0)
                                        ? "text-green"
                                        : "text-warning"
                                        }`}
                                    >
                                      {Math.round(course.details?.avgQuizScore || 0)}
                                      %/{course.requirements?.quizScore?.required || 0}
                                      %
                                    </span>
                                  </div>

                                  <div
                                    className={`requirement-item ${(course.details?.mindMaps || 0) >=
                                      (course.requirements?.mindMaps?.required || 0)
                                      ? "completed"
                                      : "in-progress"
                                      }`}
                                  >
                                    <div className="requirement-label">
                                      <svg
                                        className="requirement-icon"
                                        viewBox="0 0 24 24"
                                        fill="currentColor"
                                      >
                                        <path d="M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20A8,8 0 0,0 20,12A8,8 0 0,0 12,4M12,6A6,6 0 0,1 18,12A6,6 0 0,1 12,18A6,6 0 0,1 6,12A6,6 0 0,1 12,6M12,8A4,4 0 0,0 8,12A4,4 0 0,0 12,16A4,4 0 0,0 16,12A4,4 0 0,0 12,8Z" />
                                      </svg>
                                      Mind Maps
                                    </div>
                                    <span
                                      className={`requirement-value ${(course.details?.mindMaps || 0) >=
                                        (course.requirements?.mindMaps?.required || 0)
                                        ? "text-green"
                                        : "text-warning"
                                        }`}
                                    >
                                      {course.details?.mindMaps || 0}/
                                      {course.requirements?.mindMaps?.required || 0}
                                    </span>
                                  </div>

                                  <div
                                    className={`requirement-item ${(course.details?.audioSummaries || 0) >=
                                      (course.requirements?.audioSummaries?.required || 0)
                                      ? "completed"
                                      : "in-progress"
                                      }`}
                                  >
                                    <div className="requirement-label">
                                      <svg
                                        className="requirement-icon"
                                        viewBox="0 0 24 24"
                                        fill="currentColor"
                                      >
                                        <path d="M12,14C13.66,14 15,12.66 15,11V5C15,3.34 13.66,2 12,2C10.34,2 9,3.34 9,5V11C9,12.66 10.34,14 12,14Z" />
                                      </svg>
                                      Audio Summaries
                                    </div>
                                    <span
                                      className={`requirement-value ${(course.details?.audioSummaries || 0) >=
                                        (course.requirements?.audioSummaries?.required || 0)
                                        ? "text-green"
                                        : "text-warning"
                                        }`}
                                    >
                                      {course.details?.audioSummaries || 0}/
                                      {course.requirements?.audioSummaries?.required || 0}
                                    </span>
                                  </div>                                  <div
                                    className={`requirement-item ${(course.details?.chatHistory || 0) >=
                                      (course.requirements?.chatInteractions?.required || 0)
                                      ? "completed"
                                      : "in-progress"
                                      }`}
                                  >
                                    <div className="requirement-label">
                                      <svg
                                        className="requirement-icon"
                                        viewBox="0 0 24 24"
                                        fill="currentColor"
                                      >
                                        <path d="M12,2A10,10 0 0,0 2,12C2,13.23 2.22,14.41 2.62,15.5L1,21L6.5,19.38C7.59,19.78 8.77,20 10,20A10,10 0 0,0 20,10A10,10 0 0,0 10,0" />
                                      </svg>
                                      AI Interactions
                                    </div>
                                    <span
                                      className={`requirement-value ${(course.details?.chatHistory || 0) >=
                                        (course.requirements?.chatInteractions?.required || 0)
                                        ? "text-green"
                                        : "text-warning"
                                        }`}
                                    >
                                      {course.details?.chatHistory || 0}/
                                      {course.requirements?.chatInteractions?.required || 0}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Course Footer */}
                            <div className="course-footer">
                              {course.assessmentResult ? (
                                <div>
                                  <div className="assessment-result mb-16">
                                    <div
                                      className={`result-badge ${course.assessmentResult.passed
                                        ? "passed"
                                        : "failed"
                                        }`}
                                    >
                                      {course.assessmentResult.passed
                                        ? "PASSED"
                                        : "FAILED"}
                                    </div>
                                    <div className="result-score">
                                      Score:{" "}
                                      <strong>
                                        {Math.round(
                                          course.assessmentResult.score
                                        )}
                                        %
                                      </strong>
                                    </div>
                                    <div className="result-date">
                                      {new Date(
                                        course.assessmentResult.completedAt
                                      ).toLocaleDateString("en-US", {
                                        month: "short",
                                        day: "numeric",
                                        year: "numeric",
                                      })}
                                    </div>
                                    {!course.assessmentResult.passed && (
                                      <div className="retake-info mt-8">
                                        {retakeTimers[course.courseId]
                                          ?.canRetake ? (
                                          <div className="text-warning">
                                            Ready to retake!
                                          </div>
                                        ) : retakeTimers[course.courseId] ? (
                                          <div className="text-warning">
                                            <small>
                                              Next attempt in:{" "}
                                              {
                                                retakeTimers[course.courseId]
                                                  .hours
                                              }
                                              h{" "}
                                              {
                                                retakeTimers[course.courseId]
                                                  .minutes
                                              }
                                              m{" "}
                                              {
                                                retakeTimers[course.courseId]
                                                  .seconds
                                              }
                                              s
                                            </small>
                                          </div>
                                        ) : (
                                          <div className="text-muted">
                                            <small>
                                              ⏳ Calculating retake time...
                                            </small>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                  {course.assessmentResult.passed ? (
                                    <button
                                      className="btn btn-primary no-shadow py-10 w-100"
                                      onClick={() => handleCourseSelect(course)}
                                    >
                                      View Results
                                    </button>
                                  ) : retakeTimers[course.courseId]
                                    ?.canRetake ? (
                                    <button
                                      className="btn btn-primary no-shadow py-10 w-100"
                                      onClick={() => handleCourseSelect(course)}
                                    >
                                      Retake Assessment
                                    </button>
                                  ) : (
                                    <button
                                      className="btn btn-secondary action-button w-100"
                                      disabled
                                    >
                                      ⏳ Retake in{" "}
                                      {retakeTimers[course.courseId]?.hours ||
                                        0}
                                      h{" "}
                                      {retakeTimers[course.courseId]?.minutes ||
                                        0}
                                      m
                                    </button>
                                  )}
                                  <small
                                    className={`d-block mt-8 text-center ${course.assessmentResult.passed
                                      ? "theme-color"
                                      : "text-warning"
                                      }`}
                                  >
                                    {course.assessmentResult.passed
                                      ? "✓ Assessment completed"
                                      : "⏳ Retake available after 24 hours"}
                                  </small>
                                </div>
                              ) : course.isReady ? (
                                <div>
                                  <button
                                    className="btn btn-success action-button d-flex align-items-center justify-content-center gap-2 w-100"
                                    onClick={() => handleCourseSelect(course)}
                                  >
                                    🎯 Start Assessment
                                  </button>
                                  <small className="text-green d-block mt-2 text-center">
                                    ✓ All requirements completed
                                  </small>
                                </div>
                              ) : (
                                <div>
                                  <div className="btn-soft-primary py-10  px-15 ah-text-base text-center mb-0">
                                    <small>
                                      Complete all requirements to start
                                      certification
                                    </small>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="col-12">
                        <div className="alert alert-info">
                          <h5>No Courses Available for Certification</h5>
                          <p>
                            Complete your learning activities in enrolled
                            courses to make them available for certification
                            assessment.
                          </p>
                          <button
                            className="btn btn-primary"
                            onClick={() => navigate("/my-courses")}
                          >
                            Go to My Courses
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Pagination Component */}
                  {certificationReadyCourses &&
                    certificationReadyCourses.length > cardsPerPage && (
                      <div className="d-flex justify-content-center mt-4 custom-pagination-holder">
                        <nav
                          aria-label="Course pagination"
                          className="mx-auto text-center"
                        >
                          <div className="d-inline-flex align-items-center">
                            {/* Prev Button */}
                            <button
                              className="me-2 border-0 bg-transparent p-0 d-flex align-items-center"
                              onClick={handlePreviousPage}
                              disabled={currentPage === 1}
                            >
                              <svg
                                style={{ transform: "rotate(-90deg)" }}
                                width="12"
                                height="10"
                                viewBox="0 0 17 10"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M15.7646 8.65686L8.76465 1.65686L1.76465 8.65686"
                                  stroke="#000"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                              <span className="ms-1">Prev</span>
                            </button>

                            {/* Page Numbers */}
                            {Array.from(
                              { length: getTotalPages() },
                              (_, i) => i + 1
                            ).map((pageNumber) => (
                              <button
                                key={pageNumber}
                                className={`bg-transparent border-0 me-2 pagination-item ${currentPage === pageNumber ? "active" : ""
                                  }`}
                                onClick={() => handlePageChange(pageNumber)}
                              >
                                {pageNumber}
                              </button>
                            ))}

                            {/* Next Button */}
                            <button
                              className="border-0 bg-transparent p-0 d-flex align-items-center"
                              onClick={handleNextPage}
                              disabled={currentPage === getTotalPages()}
                            >
                              <span className="me-1">Next</span>
                              <svg
                                style={{ transform: "rotate(90deg)" }}
                                width="12"
                                height="10"
                                viewBox="0 0 17 10"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M15.7646 8.65686L8.76465 1.65686L1.76465 8.65686"
                                  stroke="#000"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </button>
                          </div>
                        </nav>
                      </div>
                    )}

                  {/* Pagination Info */}
                  {certificationReadyCourses &&
                    certificationReadyCourses.length > 0 && (
                      <div className="text-center mt-3">
                        <small className="text-muted">
                          Showing {(currentPage - 1) * cardsPerPage + 1} to{" "}
                          {Math.min(
                            currentPage * cardsPerPage,
                            certificationReadyCourses.length
                          )}{" "}
                          of {certificationReadyCourses.length} courses
                        </small>
                      </div>
                    )}
                </div>
              </>
            )}

            {/* Chat View */}
            {showChat && selectedCourse && (
              <div className="mt-32">
                {/* Course Header */}
                <div className="course-chat-header mb-4">
                  <div className="course-header-content">
                    <div className="course-info text-start">
                      <h4 className="course-title text-start">
                        {selectedCourse.title}
                      </h4>
                      <p className="course-subtitle text-start">
                        {selectedCourse.subtitle}
                      </p>
                    </div>
                    <div className="header-actions">
                      <button
                        className="btn btn-back"
                        onClick={handleBackToCourses}
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          className="me-2"
                        >
                          <path
                            d="M19 12H5M12 19L5 12L12 5"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        Back to Courses
                      </button>
                      {/* {!isAssessmentComplete && (
                        <button 
                          className="btn btn-end-assessment"
                          onClick={() => setShowEndAssessmentModal(true)}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="me-2">
                            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          End Assessment
                        </button>
                      )} */}
                    </div>
                  </div>
                </div>

                {/* Assessment Result Banner */}
                {assessmentResult && (
                  <div
                    className={`alert ${assessmentResult.passed
                      ? "alert-success"
                      : "alert-warning"
                      } mb-4`}
                  >
                    <div className="d-flex align-items-center">
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="me-2"
                      >
                        {assessmentResult.passed ? (
                          <path
                            d="M9 16.17L4.83 12L3.41 13.41L9 19L21 7L19.59 5.59L9 16.17Z"
                            fill="#28a745"
                          />
                        ) : (
                          <path
                            d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"
                            fill="#ffc107"
                          />
                        )}
                      </svg>
                      <div className="flex-grow-1">
                        <h6 className="mb-1 fw-bold">
                          {assessmentResult.passed
                            ? "Certification Passed!"
                            : "Certification Not Passed"}
                        </h6>
                        <p className="mb-2">{assessmentResult.message}</p>
                        <div className="small">
                          {/* <strong>
                            Score: {assessmentResult.averageScore}%
                          </strong> */}
                          {assessmentResult.totalQuestions && (
                            <span className="ms-3">
                              Questions: {assessmentResult.totalQuestions}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Progress Indicator */}
                {!isAssessmentComplete && questionCount > 0 && (
                  <div className="alert alert-info mb-3">
                    <div className="d-flex justify-content-between align-items-center">
                      <span>
                        Assessment Progress: {questionCount}/1 question
                      </span>
                      <div
                        className="progress"
                        style={{ width: "200px", height: "8px" }}
                      >
                        <div
                          className="progress-bar bg-primary"
                          role="progressbar"
                          style={{ width: `${(questionCount / 1) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Chat Messages */}
                {history.length === 0 ? (
                  <div className="text-center py-5">
                    {completedAssessments.has(selectedCourse.courseId) ? (
                      <>
                        <h5>Assessment Results</h5>
                        <p className="text-muted">
                          You have already completed the certification
                          assessment for <strong>{selectedCourse.title}</strong>
                          .
                        </p>
                        <p className="text-muted">
                          Your previous assessment results are displayed below.
                          Assessments cannot be retaken once completed.
                        </p>
                        <div className="alert alert-info">
                          <strong>Note:</strong> This is a read-only view of
                          your completed assessment.
                        </div>
                      </>
                    ) : (
                      <>
                        <h5>📋 Single Question Assessment</h5>
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                          <p className="text-blue-800 mb-2">
                            I'm ready to conduct your certification assessment
                            for <strong>{selectedCourse.title}</strong>.
                          </p>
                          <p className="text-blue-700 mb-1">
                            I'll ask you ONE comprehensive question to evaluate
                            your mastery of the course competencies.
                          </p>
                          <p className="text-sm text-blue-600 mb-0">
                            Provide a detailed answer to demonstrate your
                            understanding. You need <strong>80% to pass</strong>
                            .
                          </p>
                        </div>
                        {isAutoTyping && (
                          <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-3">
                            <p className="text-green-700 text-sm mb-0">
                              <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                                aria-hidden="true"
                              ></span>
                              Auto-typing "Start my assessment" for you... Press
                              Enter when ready!
                            </p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ) : (
                  <div
                    className="chat-container px-4 py-2"
                    ref={chatContainerRef}
                  >
                    <div className="chat-wrapper">
                      {history.map((message, index) => (
                        <div
                          key={index}
                          className={`message ${message.type === "user"
                            ? "user-message"
                            : "bot-message"
                            }`}
                        >
                          {message.type === "user" ? (
                            <>
                              <div className="flex-shrink-0">
                                <Image
                                  src={
                                    auth.profilePic?.startsWith("/")
                                      ? process.env.REACT_APP_GALLERY_URL +
                                      "/" +
                                      auth.profilePic
                                      : process.env.REACT_APP_GALLERY_URL +
                                      auth.profilePic
                                  }
                                  alt="Profile"
                                  className="user-img"
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = userProfileImage;
                                  }}
                                />
                              </div>
                              <div className="user-bubble text-start">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                  <span>You</span>
                                  <small className="text-muted">
                                    {message.timestamp
                                      ? new Date(
                                        message.timestamp
                                      ).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })
                                      : ""}
                                  </small>
                                </div>
                                {message.isAudio ? (
                                  <div className="d-flex align-items-center gap-2 p-2 bg-light rounded">
                                    <svg
                                      width="20"
                                      height="20"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <path
                                        d="M12 14C13.66 14 15 12.66 15 11V5C15 3.34 13.66 2 12 2C10.34 2 9 3.34 9 5V11C9 12.66 10.34 14 12 14Z"
                                        fill="#007bff"
                                      />
                                      <path
                                        d="M19 11C19 15.42 15.42 19 11 19H13C17.42 19 21 15.42 21 11H19Z"
                                        fill="#007bff"
                                      />
                                      <path
                                        d="M5 11C5 15.42 8.58 19 13 19H11C6.58 19 3 15.42 3 11H5Z"
                                        fill="#007bff"
                                      />
                                    </svg>
                                    <div>
                                      <div className="fw-bold text-primary">
                                        Audio Message
                                      </div>
                                      <small className="text-muted">
                                        {message.audioDuration || 0}s
                                      </small>
                                    </div>
                                  </div>
                                ) : (
                                  message.text.split("\n").map((line, i) => (
                                    <p key={i} className="mb-2">
                                      {line}
                                    </p>
                                  ))
                                )}
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="bot-icon">
                                <svg
                                  width="20"
                                  height="21"
                                  viewBox="0 0 20 21"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M19 9.81814V6.84314C19 6.31271 18.7893 5.804 18.4142 5.42893C18.0391 5.05386 17.5304 4.84314 17 4.84314H11V3.53114C11.305 3.25714 11.5 2.86314 11.5 2.42114C11.5 2.02332 11.342 1.64179 11.0607 1.36048C10.7794 1.07918 10.3978 0.921143 10 0.921143C9.60218 0.921143 9.22064 1.07918 8.93934 1.36048C8.65804 1.64179 8.5 2.02332 8.5 2.42114C8.5 2.86314 8.695 3.25714 9 3.53114V4.84314H3C2.46957 4.84314 1.96086 5.05386 1.58579 5.42893C1.21071 5.804 1 6.31271 1 6.84314V9.84114L0.928 9.84614C0.675973 9.86434 0.440176 9.97726 0.268018 10.1622C0.0958586 10.3472 0.000102245 10.5905 0 10.8431V12.8431C0 13.1084 0.105357 13.3627 0.292893 13.5502C0.48043 13.7378 0.734784 13.8431 1 13.8431V18.8431C1 19.3736 1.21071 19.8823 1.58579 20.2574C1.96086 20.6324 2.46957 20.8431 3 20.8431H17C17.5304 20.8431 18.0391 20.6324 18.4142 20.2574C18.7893 19.8823 19 19.3736 19 18.8431V13.8431C19.2652 13.8431 19.5196 13.7378 19.7071 13.5502C19.8946 13.3627 20 13.1084 20 12.8431V10.9051C20.0117 10.7499 19.9871 10.5941 19.928 10.4501C19.726 9.96214 19.293 9.84514 19 9.81814ZM5 10.8431C5 9.73914 5.672 8.84314 6.5 8.84314C7.328 8.84314 8 9.73914 8 10.8431C8 11.9471 7.328 12.8431 6.5 12.8431C5.672 12.8431 5 11.9471 5 10.8431ZM13.998 16.8431C12.997 16.8401 6 16.8431 6 16.8431V14.8431C6 14.8431 13.001 14.8411 14.002 14.8431L13.998 16.8431ZM13.5 12.8431C12.672 12.8431 12 11.9471 12 10.8431C12 9.73914 12.672 8.84314 13.5 8.84314C14.328 8.84314 15 9.73914 15 10.8431C15 11.9471 14.328 12.8431 13.5 12.8431Z"
                                    fill="white"
                                  />
                                </svg>
                              </div>
                              <div className="bot-bubble d-flex flex-column text-start">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                  <span>CertifyMe.AI</span>
                                  <small className="text-muted">
                                    {message.timestamp
                                      ? new Date(
                                        message.timestamp
                                      ).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })
                                      : ""}
                                  </small>
                                </div>
                                <ReactMarkdown>{message.text}</ReactMarkdown>
                                {message.competencyArea && (
                                  <div className="mt-2">
                                    <small className="text-muted">
                                      <strong>Competency:</strong>{" "}
                                      {message.competencyArea}
                                    </small>
                                  </div>
                                )}
                                {/* {message.score && (
                                  <div className="mt-1">
                                    <small className="text-muted">
                                      <strong>Score:</strong> {message.score}%
                                    </small>
                                  </div>
                                )} */}
                              </div>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Input and Submit Button */}
                {isLoading && (
                  <div className="text-center mt-4 mb-4">
                    <div className="dottedLoading theme-color"></div>
                    <p className="mt-2 text-muted">
                      AI is analyzing your answer and evaluating your
                      performance...
                    </p>
                  </div>
                )}
                {isError && (
                  <Alert variant="danger">{isError}</Alert>
                )}

                {/* Assessment Completed Message */}
                {isAssessmentComplete && (
                  <div className="text-center py-4">
                    <div className="alert alert-info">
                      <h6 className="mb-2">Assessment Completed</h6>
                      <p className="mb-0">
                        This assessment has been completed. You cannot continue
                        chatting for this course.
                      </p>
                    </div>
                  </div>
                )}

                {!completedAssessments.has(selectedCourse.courseId) &&
                  !isAssessmentComplete && (
                    <div className="certifyMe-search-box-wrapper mx-auto mt-22 overflow-hidden px-9 py-17 mb-32">
                      <div className="position-relative d-flex align-items-center">
                        {/* Wave Animation - Left side of input */}
                        {isRecording && (
                          <div
                            className="wave-animation position-absolute"
                            style={{ left: "15px", zIndex: 10 }}
                          >
                            <div className="wave-bar"></div>
                            <div className="wave-bar"></div>
                            <div className="wave-bar"></div>
                            <div className="wave-bar"></div>
                            <div className="wave-bar"></div>
                          </div>
                        )}

                        <textarea
                          ref={textareaRef}
                          className={`form-control ah-text-base text-black flex-grow-1 ${isRecording ? "ps-5" : ""
                            } ${isAutoTyping ? "auto-typing" : ""}`}
                          placeholder={
                            isRecording
                              ? `Recording... ${audioDuration}s`
                              : isAutoTyping
                                ? "Auto-typing..."
                                : "Type your answer..."
                          }
                          value={isAutoTyping ? autoTypedText : input}
                          onChange={handleInputChange}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                              if (!isAutoTyping) {
                                e.preventDefault();
                                handleSendClick();
                              }
                            }
                          }}
                          disabled={isProcessing || isAutoTyping}
                          style={{
                            paddingLeft: isRecording ? "60px" : "15px",
                            paddingRight: "110px",
                            minHeight: "85px",
                            maxHeight: "200px",
                            resize: "none",
                            overflowY: "auto",
                            lineHeight: "1.5",
                            paddingTop: "10px",
                            paddingBottom: "10px"
                          }}
                          rows={1}
                        />

                        {/* Microphone Button */}
                        <button
                          className={`border-0 rounded-circle text-white py-5 mx-5 position-absolute ${isRecording
                            ? "bg-danger"
                            : isProcessing
                              ? "bg-green"
                              : "bg-secondary"
                            }`}
                          type="button"
                          onClick={handleMicrophoneClick}
                          disabled={isLoading || !token || isProcessing}
                          style={{ right: "60px" }}
                          title={
                            isRecording
                              ? "Stop Recording"
                              : isProcessing
                                ? "Processing..."
                                : "Click to speak"
                          }
                        >
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M12 14C13.66 14 15 12.66 15 11V5C15 3.34 13.66 2 12 2C10.34 2 9 3.34 9 5V11C9 12.66 10.34 14 12 14Z"
                              fill="white"
                            />
                            <path
                              d="M19 11C19 15.42 15.42 19 11 19H13C17.42 19 21 15.42 21 11H19Z"
                              fill="white"
                            />
                            <path
                              d="M5 11C5 15.42 8.58 19 13 19H11C6.58 19 3 15.42 3 11H5Z"
                              fill="white"
                            />
                            <path
                              d="M12 22C12.55 22 13 21.55 13 21V19H11V21C11 21.55 11.45 22 12 22Z"
                              fill="white"
                            />
                          </svg>
                        </button>

                        {/* Send Button */}
                        <button
                          className={`border-0 rounded-circle text-white py-5 mx-10 position-absolute ${isRecording || audioDuration > 0
                            ? "bg-success"
                            : "bg-theme"
                            }`}
                          type="button"
                          onClick={handleSendClick}
                          disabled={isLoading || !token || isProcessing}
                          style={{ right: "10px" }}
                          title={
                            isRecording
                              ? "Stop & Send Audio"
                              : audioDuration > 0
                                ? "Send Audio"
                                : "Send Message"
                          }
                        >
                          <svg
                            width="25"
                            height="25"
                            viewBox="0 0 25 25"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M10.2656 4.84314C9.4372 4.84314 8.76562 5.51471 8.76562 6.34314V19.3431C8.76562 20.1715 9.4372 20.8431 10.2656 20.8431C11.094 20.8431 11.7656 20.1715 11.7656 19.3431V6.34314C11.7656 5.51471 11.094 4.84314 10.2656 4.84314Z"
                              fill="white"
                            />
                            <path
                              d="M13.7656 9.34314C13.7656 8.51471 14.4372 7.84314 15.2656 7.84314C16.094 7.84314 16.7656 8.51471 16.7656 9.34314V16.3431C16.7656 17.1715 16.094 17.8431 15.2656 17.8431C14.4372 17.8431 13.7656 17.1715 13.7656 16.3431V9.34314Z"
                              fill="white"
                            />
                            <path
                              d="M5.26562 9.84314C4.4372 9.84314 3.76562 10.5147 3.76562 11.3431V14.3431C3.76562 15.1715 4.4372 15.8431 5.26562 15.8431C6.09405 15.8431 6.76562 15.1715 6.76562 14.3431V11.3431C6.76562 10.5147 6.09405 9.84314 5.26562 9.84314Z"
                              fill="white"
                            />
                            <path
                              d="M20.2656 9.84314C19.4372 9.84314 18.7656 10.5147 18.7656 11.3431V14.3431C18.7656 15.1715 19.4372 15.8431 20.2656 15.8431C21.094 15.1715 21.7656 14.3431V11.3431C21.7656 10.5147 21.094 9.84314 20.2656 9.84314Z"
                              fill="white"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                  )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Warning Modal for Completed Assessments */}
      {showWarningModal && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">⚠️ Assessment Already Completed</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCancelEndAssessment}
                ></button>
              </div>
              <div className="modal-body">
                <p>
                  You have already completed the certification assessment for{" "}
                  <strong>{selectedCourseForWarning?.title}</strong>.
                </p>
                <p>
                  <strong>Note:</strong> Assessments cannot be retaken once
                  completed. You can only view your previous results.
                </p>
                <div className="alert alert-warning">
                  <strong>Warning:</strong> If you proceed, you will only be
                  able to view your previous assessment results, not retake the
                  assessment.
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCancelEndAssessment}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary "
                  onClick={handleConfirmEndAssessment}
                >
                  View Results
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* End Assessment Confirmation Modal */}
      {showEndAssessmentModal && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">⚠️ End Assessment</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCancelEndAssessmentModal}
                ></button>
              </div>
              <div className="modal-body">
                <p>
                  Are you sure you want to end the certification assessment for{" "}
                  <strong>{selectedCourse?.title}</strong>?
                </p>
                <p>
                  <strong>Warning:</strong> Once ended, this assessment will be
                  marked as incomplete and you will not be able to retake it.
                </p>
                <div className="alert alert-warning">
                  <strong>Note:</strong> Ending the assessment now will result
                  in a failed certification attempt.
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCancelEndAssessmentModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleConfirmEndAssessmentModal}
                >
                  End Assessment
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default StudentCeritifyMe;
